import { isAnchorBlock, parseAnchorBlock } from "@/lib/anchorNames";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, History, MessageSquarePlus, Send, Truck, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { BulkNoteDialog } from "@/components/panel/BulkNoteDialog";
import { CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOffline } from "@/context/OfflineContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { WorkflowSection } from "@/components/panel/WorkflowSection";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function Section({ title, children, testId }) {
  return (
    <section className="border-t border-slate-300 px-4 py-4" data-testid={testId}>
      <h3 className="mono mb-3 text-[10px] uppercase tracking-[0.22em] text-slate-500">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span className="text-[11px] text-slate-500">{label}</span>
      <span className="mono truncate text-[11.5px] text-slate-800">{value || "—"}</span>
    </div>
  );
}

/** Closed issues read as resolved; the current user's own issues stay prominent. */
function issueCardClass(issue, mine) {
  if (issue.status === "Closed") return "border-emerald-300 bg-emerald-50";
  if (mine) return "border-orange-400 bg-orange-50 shadow-sm";
  return "border-slate-200 bg-slate-50 opacity-70";
}

export function PanelProperties({
  projectId,
  panel,
  pallets,
  selection = [],
  onClose,
  onChanged,
  onCreateIssue,
}) {
  const [bulkNote, setBulkNote] = useState(false);
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [comments, setComments] = useState([]);
  const [issues, setIssues] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [showComment, setShowComment] = useState(false);
  const [mainTab, setMainTab] = useState("properties");
  const { user, capabilities } = useAuth();
  const { submit, isPendingPanel } = useOffline();

  const isMine = (issue) => (issue.assignee_ids || []).includes(user?.id);

  const [width, setWidth] = useState(() => Number(localStorage.getItem("okn-panel-width")) || 370);
  const { isCompact } = useIsMobile();

  const startResize = (event) => {
    const startX = event.clientX;
    const startWidth = width;
    let latest = width;
    const move = (e) => {
      latest = Math.min(Math.max(startWidth + (startX - e.clientX), 300), 780);
      setWidth(latest);
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      localStorage.setItem("okn-panel-width", String(latest));
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const loadSide = async () => {
    if (!panel?.panel_uid) {
      setHistory([]);
      setComments([]);
      setIssues([]);
      return;
    }
    try {
      const [h, c, i] = await Promise.all([
        api.get(`/projects/${projectId}/panels/${panel.panel_uid}/history`),
        api.get(`/projects/${projectId}/panels/${panel.panel_uid}/comments`),
        api.get(`/projects/${projectId}/issues`),
      ]);
      setHistory(h.data);
      setComments(c.data);
      setIssues((i.data.issues || []).filter((x) => x.panel_uid === panel.panel_uid));
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  useEffect(() => {
    loadSide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panel?.panel_uid, panel?.updated_at]);

  const submitComment = async () => {
    const result = await submit({
      method: "post",
      path: `/projects/${projectId}/panels/${panel.panel_uid}/comments`,
      body: { text: commentText },
      label: `Note on ${panel.panel_name || panel.panel_uid}`,
      panel_uid: panel.panel_uid,
    });
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    setCommentText("");
    setShowComment(false);
    if (result.queued) {
      toast.success("Note queued offline");
      return;
    }
    loadSide();
    toast.success("Comment added");
  };

  if (!panel) return null;

  return (
    <aside
      data-testid="panel-properties"
      style={isCompact ? undefined : { width }}
      className="okn-scroll absolute right-0 top-0 z-20 flex h-full max-w-full flex-col overflow-y-auto border-l border-slate-300 bg-[#DFE5EE] max-lg:inset-x-0 max-lg:bottom-0 max-lg:top-auto max-lg:h-[68vh] max-lg:w-screen max-lg:rounded-t-2xl max-lg:border-l-0 max-lg:border-t max-lg:shadow-2xl"
    >
      <div
        data-testid="panel-resize-handle"
        onMouseDown={startResize}
        title="Drag to resize"
        className="absolute left-0 top-0 z-30 h-full w-1.5 cursor-col-resize bg-transparent hover:bg-cyan-400/50"
      />
      <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-slate-300 bg-[#DFE5EE] px-4 py-4">
        <div className="min-w-0">
          {panel.multi ? (
            <>
              <p
                data-testid="panel-multi-header"
                className="mono truncate text-sm text-cyan-700"
              >
                {panel.selected_count} Panels Selected
              </p>
              <p className="mono mt-1 truncate text-[10px] text-slate-500">
                Shared values shown · differences marked “Varies”
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button
                  data-testid="multi-add-note-button"
                  onClick={() => setBulkNote(true)}
                  className="mono flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[9.5px] uppercase tracking-wider text-slate-700 hover:border-cyan-400 hover:text-cyan-700"
                >
                  <MessageSquarePlus size={11} /> Add note
                </button>
                <button
                  data-testid="multi-add-issue-button"
                  onClick={() => onCreateIssue?.(panel)}
                  className="mono flex items-center gap-1 rounded-full border border-amber-500/50 bg-white px-2.5 py-1 text-[9.5px] uppercase tracking-wider text-amber-700 hover:bg-amber-500/10"
                >
                  <AlertTriangle size={11} /> Add issue
                </button>
              </div>
            </>
          ) : isAnchorBlock(panel.block_definition) ? (
            <>
              <p
                data-testid="anchor-header-definition"
                className="mono truncate text-sm text-cyan-700"
              >
                {panel.block_definition}
              </p>
              <p className="mono mt-1 truncate text-[10px] text-slate-500">{panel.panel_name}</p>
            </>
          ) : (
            <>
              <p className="mono truncate text-sm text-cyan-700">{panel.panel_name}</p>
              <p className="mono mt-1 truncate text-[10px] text-slate-500">{panel.panel_uid}</p>
              {isPendingPanel?.(panel.panel_uid) ? (
                <span
                  data-testid="panel-pending-sync-badge"
                  className="mono mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] uppercase tracking-wider text-amber-700"
                >
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" /> Pending
                  sync
                </span>
              ) : null}
            </>
          )}
        </div>
        <button
          data-testid="close-properties-button"
          onClick={onClose}
          className="okn-tool grid h-7 w-7 shrink-0 place-items-center rounded-md text-slate-600 hover:bg-slate-100"
        >
          <X size={15} />
        </button>
      </div>

      <div
        className={`flex gap-1.5 border-b border-slate-300 bg-[#DFE5EE] px-4 py-3 ${
          panel.multi ? "hidden" : ""
        }`}
      >
        {[
          ["properties", "Info", false, ""],
          ["comments", `Notes (${comments.length})`, comments.length > 0, "amber"],
          ["issues", `Issues (${issues.length})`, issues.length > 0, "red"],
          ["history", `Log (${history.length})`, false, ""],
        ].map(([key, label, hasItems, tone]) => (
          <button
            key={key}
            data-testid={`panel-main-tab-${key}`}
            onClick={() => setMainTab(key)}
            className={`mono flex-1 whitespace-nowrap rounded-full px-1.5 py-1.5 text-[9.5px] uppercase tracking-[0.06em] transition-colors ${
              mainTab === key
                ? "bg-cyan-600/15 text-cyan-700"
                : hasItems
                  ? tone === "red"
                    ? "okn-tab-pulse-red border border-red-400 bg-red-500/8 text-red-700"
                    : "okn-tab-pulse-amber border border-amber-400 bg-amber-400/12 text-amber-700"
                  : "border border-slate-200 text-slate-600 hover:bg-slate-100"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {panel.multi || mainTab === "properties" ? (
      <div data-testid="panel-properties-tab">
      {isAnchorBlock(panel.block_definition) ? (
        <AnchorInfo panel={panel} />
      ) : (
      <Section title="Panel" testId="panel-info-section">
        <Row label="Project Code" value={panel.project_code} />
        <Row label="Elevation" value={panel.elevation} />
        <Row label="Level" value={panel.level} />
        <Row label="Type Code" value={panel.type_code} />
        <Row label="Panel Code" value={panel.panel_code} />
        <Row label="Panel Type" value={panel.panel_type} />
        <Row label="Unique Code" value={panel.unique_code} />
        <Row label="Block Definition" value={panel.block_definition} />
        <Row label="Rhino Object ID" value={panel.source_object_id?.slice(0, 13)} />
      </Section>
      )}

      <Section title="Workflow">
        <WorkflowSection
          projectId={projectId}
          panel={panel}
          pallets={pallets}
          onChanged={onChanged}
          readOnly
        />
      </Section>

      </div>
      ) : mainTab === "history" ? (
      <Section title="History">
        <div className="space-y-2.5" data-testid="panel-history">
          {history.length === 0 ? (
            <p className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <History size={12} /> No status changes yet.
            </p>
          ) : (
            history.map((row) => (
              <div
                key={`${row.changed_at}-${row.department_label}-${row.new_status}`}
                className="border-l border-slate-200 pl-3"
              >
                <p className="mono text-[9.5px] uppercase tracking-wider text-slate-500">
                  {new Date(row.changed_at).toLocaleString()}
                </p>
                <p className="mt-0.5 text-[11.5px] text-slate-700">
                  <b className="text-slate-900">{row.department_label}</b>{" "}
                  {row.old_status || "—"} → <b className="text-cyan-700">{row.new_status || "—"}</b>
                </p>
                {row.department === "logistics" &&
                (row.shipment_code || (!panel.multi && panel.shipment_code)) ? (
                  <button
                    data-testid={`log-shipment-link-${row.shipment_code || panel.shipment_code}`}
                    onClick={() =>
                      navigate(
                        `/projects/${projectId}/logistics?highlight=${encodeURIComponent(
                          row.shipment_code || panel.shipment_code
                        )}`
                      )
                    }
                    className="mono mt-0.5 flex items-center gap-1 text-[10.5px] text-cyan-700 underline decoration-dotted hover:text-cyan-600"
                  >
                    <Truck size={11} /> {row.shipment_code || panel.shipment_code}
                  </button>
                ) : null}
                {row.reason ? (
                  <p className="mt-0.5 text-[11px] italic text-amber-700">{row.reason}</p>
                ) : null}
                <p className="mono mt-0.5 text-[9.5px] text-slate-500">{row.changed_by}</p>
              </div>
            ))
          )}
        </div>
      </Section>
      ) : mainTab === "comments" ? (
      <Section title="Comments" testId="panel-comments-section">
          <div data-testid="panel-comments-tab">
            <Button
              data-testid="add-comment-button"
              onClick={() => setShowComment((v) => !v)}
              variant="outline"
              className={`h-8 w-full rounded-full border-slate-300 bg-transparent text-[10px] uppercase tracking-wider text-slate-700 hover:bg-slate-100 ${
                capabilities?.is_guest ? "hidden" : ""
              }`}
            >
              <MessageSquarePlus size={12} className="mr-1" /> Add comment
            </Button>
            {showComment ? (
              <div className="mt-3">
                <Textarea
                  data-testid="comment-input"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add an informal note about this panel…"
                  className="min-h-20 border-slate-200 bg-[#F8FAFC] text-xs"
                />
                <Button
                  data-testid="comment-submit-button"
                  onClick={submitComment}
                  disabled={!commentText.trim()}
                  className="mt-2 h-8 w-full rounded-full bg-cyan-600 text-[11px] text-white"
                >
                  <Send size={12} className="mr-1" /> Post comment
                </Button>
              </div>
            ) : null}
            <div className="mt-3 space-y-2">
              {comments.length === 0 ? (
                <p className="text-[11px] text-slate-500">No comments yet.</p>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.id}
                    data-testid="panel-comment"
                    className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-3"
                  >
                    <p className="text-[11.5px] leading-relaxed text-slate-800">{comment.text}</p>
                    <p className="mono mt-1.5 text-[9.5px] uppercase tracking-wider text-slate-500">
                      {comment.created_by} · {new Date(comment.created_at).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
      </Section>
      ) : (
      <Section title="Issues" testId="panel-issues-section">
          <div data-testid="panel-issues-tab">
            <Button
              data-testid="create-issue-button"
              onClick={() => onCreateIssue?.(panel)}
              variant="outline"
              className={`h-8 w-full rounded-full border-amber-500/45 bg-transparent text-[10px] uppercase tracking-wider text-amber-700 hover:bg-amber-500/10 ${
                capabilities?.is_guest ? "hidden" : ""
              }`}
            >
              Create issue
            </Button>
            <div className="mt-3 space-y-2">
              {issues.length === 0 ? (
                <p className="text-[11px] text-slate-500">No issues for this panel.</p>
              ) : (
                issues.map((issue) => (
                  <div
                    key={issue.id}
                    data-testid={isMine(issue) ? "panel-issue-mine" : "panel-issue"}
                    className={`rounded-lg border p-3 ${issueCardClass(issue, isMine(issue))}`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`mono text-[10px] ${
                          isMine(issue) ? "font-semibold text-orange-700" : "text-slate-500"
                        }`}
                      >
                        {issue.issue_key}
                      </span>
                      {isMine(issue) ? (
                        <span className="mono rounded bg-orange-500 px-1.5 text-[9px] uppercase tracking-wider text-white">
                          Mine
                        </span>
                      ) : null}
                      {issue.status === "Closed" ? (
                        <CheckCircle2 size={13} className="ml-auto text-emerald-700" />
                      ) : null}
                      {issue.issue_type === "NCR" ? (
                        <span className="mono rounded bg-red-500/15 px-1.5 text-[9px] uppercase tracking-wider text-red-700">
                          NCR
                        </span>
                      ) : null}
                      <span className="mono ml-auto text-[9px] uppercase tracking-wider text-slate-500">
                        {issue.status}
                      </span>
                    </div>
                    <p
                      className={`mt-1 text-[11.5px] ${
                        isMine(issue) ? "font-medium text-slate-900" : "text-slate-600"
                      }`}
                    >
                      {issue.title}
                    </p>
                    <p className="mono mt-1 text-[9.5px] uppercase tracking-wider text-slate-500">
                      {issue.priority} · {issue.assigned_to_name || "Unassigned"}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
      </Section>
      )}
      <BulkNoteDialog
        projectId={projectId}
        panelUids={selection}
        open={bulkNote}
        onOpenChange={setBulkNote}
        onDone={onChanged}
      />
    </aside>
  );
}

/** Anchor Info: Project Code / Anchor Category / Type Code / Block Definition / Rhino Object ID. */
function AnchorInfo({ panel }) {
  const anchor = parseAnchorBlock(panel.block_definition);
  return (
    <Section title="Anchor" testId="anchor-info-section">
      <Row label="Project Code" value={anchor.projectCode} />
      <Row label="Anchor Category" value={anchor.anchorCategory} />
      <Row label="Type Code" value={anchor.typeCode} />
      <Row label="Block Definition" value={panel.block_definition} />
      <Row label="Rhino Object ID" value={panel.source_object_id?.slice(0, 13)} />
    </Section>
  );
}

